import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { Button } from '../components/arc/button/button';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { ArcPage, ArcPageHeader, ArcPanel, ArcTableFrame, ArcTabs } from '../components/arc/primitives/arc-primitives';
import {
  getSenders, getMailTemplates, getModalTemplates, getPartners, getSegments,
  createSegment, updateSegment, deactivateSegment, getSegmentEstimate, createSender, updateSender,
  type Sender, type SenderInput, type MailTemplate, type ModalTemplate, type Partner, type Segment,
  type SegmentRuleInput,
} from '../api/marketing';
import './marketing-mailing-settings-arc.css';

export type MailingTab = 'senders' | 'mail' | 'modal' | 'partners' | 'segments';
type Tab = MailingTab;
const TABS: { key: Tab; label: string }[] = [
  { key: 'senders', label: 'Odesílatelé' },
  { key: 'mail', label: 'Mail šablony' },
  { key: 'modal', label: 'Modal šablony' },
  { key: 'partners', label: 'Partneři (URL)' },
  { key: 'segments', label: 'Segmenty' },
];
const FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };

export function MarketingMailingSettings({ onBack, initialTab }: { onBack: () => void; initialTab?: Tab }) {
  const [tab, setTab] = useState<Tab>(initialTab ?? 'senders');
  function selectTab(next: Tab) {
    const url = new URL(window.location.href);
    url.searchParams.set('mkt', next);
    window.history.replaceState({}, '', url.toString());
    setTab(next);
  }
  return (
    <ArcPage className="p1-mkt-settings">
      <ArcPageHeader eyebrow="RONDO" title="Marketing" description="Odesílatelé, šablony, partneři a segmenty pro komunikaci." meta={<Button type="button" variant="ghost" size="sm" onClick={onBack}>Zpět na Nastavení</Button>} />
      <ArcTabs items={TABS.map((t) => ({ value: t.key, label: t.label }))} value={tab} onChange={selectTab} label="Nastavení marketingu" />
      {tab === 'senders' && <SendersTab />}
      {tab === 'mail' && <MailTab />}
      {tab === 'modal' && <ModalTab />}
      {tab === 'partners' && <PartnersTab />}
      {tab === 'segments' && <SegmentsTab />}
    </ArcPage>
  );
}

function useList<T>(fetcher: () => Promise<T[]>): { data: T[] | null; error: string | null } {
  const [data, setData] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetcher()
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { data, error };
}

const Loading = ({ error }: { error: string | null }) =>
  error ? <div className="alert alert-danger">{error}</div> : <div className="text-muted py-3">Načítám…</div>;

function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) return <span className="badge bg-light text-muted border" style={{ fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '.66rem' }}>- score</span>;
  const cls = score >= 0.95 ? { bg: 'rgba(52,199,89,.12)', c: '#2ca84b' } : score >= 0.85 ? { bg: 'rgba(255,149,0,.12)', c: '#a35a00' } : { bg: 'rgba(200,43,35,.12)', c: '#c82b23' };
  return <span style={{ fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '.66rem', padding: '1px 6px', borderRadius: 6, background: cls.bg, color: cls.c }}>{score.toFixed(4)}</span>;
}

const PROVIDER_LABEL: Record<Sender['provider'], string> = { mailkit: 'Mailkit', sendgrid: 'SendGrid' };
const senderCountLabel = (count: number) => `${count} ${count === 1 ? 'odesílatel' : count >= 2 && count <= 4 ? 'odesílatelé' : 'odesílatelů'}`;

function SenderCard({ s, onEdit }: { s: Sender; onEdit: () => void }) {
  return (
    <div className="p1-mkt-sender-card" data-active={s.active}>
      <div className="p1-mkt-sender-name">
        <strong>{s.name}</strong>
        {s.isDefault && <span className="p1-mkt-tag">Výchozí</span>}
        {!s.active && <span className="p1-mkt-tag">Vypnutý</span>}
      </div>
      <div className="p1-mkt-sender-email">{s.email}</div>
      <div className="p1-mkt-sender-meta">
        <span className="p1-mkt-tag">{PROVIDER_LABEL[s.provider]}</span>
        {s.provider === 'sendgrid' && s.warmupLimit != null && <span>Strop {s.warmupLimit.toLocaleString('cs-CZ')}</span>}
        {s.mailkitSenderId && <span>MK#{s.mailkitSenderId}</span>}
        <ScoreBadge score={s.deliverabilityScore} />
        <span className="p1-mkt-sender-verified" data-verified={s.verified}>{s.verified ? 'DKIM/SPF ověřeno' : 'Neověřeno'}</span>
      </div>
      <Button type="button" variant="secondary" size="sm" onClick={onEdit}>Upravit</Button>
    </div>
  );
}

function SenderEditor({ sender, onDone, onCancel }: { sender: Sender | null; onDone: () => void; onCancel: () => void }) {
  const [form, setForm] = useState<SenderInput>({
    country: sender?.country ?? 'CZ',
    purpose: sender?.purpose ?? 'campaign',
    provider: sender?.provider ?? 'sendgrid',
    name: sender?.name ?? '',
    email: sender?.email ?? '',
    replyTo: sender?.replyTo ?? null,
    isDefault: sender?.isDefault ?? false,
    active: sender?.active ?? true,
    warmupLimit: sender?.warmupLimit ?? null,
    mailkitCampaignId: sender?.mailkitCampaignId ?? null,
    mailkitMailinglistId: sender?.mailkitMailinglistId ?? null,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (patch: Partial<SenderInput>) => setForm((f) => ({ ...f, ...patch }));
  const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

  async function save() {
    if (!form.name.trim() || !form.email.trim()) { setErr('Jméno a e-mail odesílatele jsou povinné'); return; }
    setSaving(true); setErr(null);
    try {
      if (sender) await updateSender(sender.id, form); else await createSender(form);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Uložení selhalo'); setSaving(false);
    }
  }

  return (
    <ArcPanel className="p1-mkt-editor">
      <h2>{sender ? `Upravit odesílatele: ${sender.name}` : 'Nový odesílatel'}</h2>
      {err && <div className="alert alert-danger py-2">{err}</div>}
      <div className="row g-2 mb-2">
        <div className="col-sm-6"><label className="form-label small text-muted mb-1">Jméno odesílatele *</label>
          <input className="form-control form-control-sm" value={form.name} onChange={(e) => set({ name: e.target.value })} /></div>
        <div className="col-sm-3"><label className="form-label small text-muted mb-1">Trh</label>
          <CompactSelect value={form.country ?? 'CZ'} ariaLabel="Trh" options={['CZ', 'SK', 'RO'].map((v) => ({ value: v, label: `${FLAG[v]} ${v}` }))} onChange={(v) => set({ country: v })} /></div>
        <div className="col-sm-3"><label className="form-label small text-muted mb-1">Účel</label>
          <CompactSelect value={form.purpose} ariaLabel="Účel" options={[{ value: 'campaign', label: 'Kampaně' }, { value: 'notification', label: 'Notifikace' }]} onChange={(v) => set({ purpose: v as SenderInput['purpose'] })} /></div>
      </div>
      <div className="row g-2 mb-2">
        <div className="col-sm-6"><label className="form-label small text-muted mb-1">E-mail odesílatele *</label>
          <input type="email" className="form-control form-control-sm" value={form.email} onChange={(e) => set({ email: e.target.value })} /></div>
        <div className="col-sm-6"><label className="form-label small text-muted mb-1">Odpovědi na</label>
          <input type="email" className="form-control form-control-sm" value={form.replyTo ?? ''} onChange={(e) => set({ replyTo: e.target.value || null })} /></div>
      </div>
      <div className="row g-2 mb-3">
        <div className="col-sm-4"><label className="form-label small text-muted mb-1">Poskytovatel</label>
          <CompactSelect value={form.provider} ariaLabel="Poskytovatel" options={[{ value: 'sendgrid', label: 'SendGrid' }, { value: 'mailkit', label: 'Mailkit' }]} onChange={(v) => set({ provider: v as SenderInput['provider'] })} /></div>
        {form.provider === 'sendgrid' ? (
          <div className="col-sm-8"><label className="form-label small text-muted mb-1">Strop příjemců na kampaň (zahřívání)</label>
            <input type="number" min={0} className="form-control form-control-sm" style={{ maxWidth: 200 }} value={form.warmupLimit ?? ''} placeholder="bez omezení" onChange={(e) => set({ warmupLimit: num(e.target.value) })} />
            <div className="text-muted mt-1" style={{ fontSize: '.72rem' }}>Kampaň odejde jen nejaktivnějším příjemcům do této výše. Prázdné = celý segment.</div></div>
        ) : (
          <>
            <div className="col-sm-4"><label className="form-label small text-muted mb-1">Mailkit ID kampaně *</label>
              <input type="number" min={0} className="form-control form-control-sm" value={form.mailkitCampaignId ?? ''} onChange={(e) => set({ mailkitCampaignId: num(e.target.value) })} /></div>
            <div className="col-sm-4"><label className="form-label small text-muted mb-1">Mailkit ID seznamu *</label>
              <input type="number" min={0} className="form-control form-control-sm" value={form.mailkitMailinglistId ?? ''} onChange={(e) => set({ mailkitMailinglistId: num(e.target.value) })} /></div>
          </>
        )}
      </div>
      <div className="d-flex gap-4 mb-3">
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" id="sender-active" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
          <label className="form-check-label small" htmlFor="sender-active">Aktivní</label>
        </div>
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" id="sender-default" checked={form.isDefault} onChange={(e) => set({ isDefault: e.target.checked })} />
          <label className="form-check-label small" htmlFor="sender-default">Výchozí pro trh a účel</label>
        </div>
      </div>
      <div className="p1-mkt-editor-actions">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={saving}>Zrušit</Button>
        <Button type="button" size="sm" onClick={save} disabled={saving}>{saving ? 'Ukládám…' : 'Uložit'}</Button>
      </div>
    </ArcPanel>
  );
}

const PURPOSE_GROUPS: { key: 'notification' | 'campaign'; label: string }[] = [
  { key: 'notification', label: 'Notifikace' },
  { key: 'campaign', label: 'Kampaně' },
];

function SendersTab() {
  const [data, setData] = useState<Sender[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Sender | 'new' | null>(null);

  function reload() {
    getSenders()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(() => { reload(); }, []);

  if (editing) {
    return <SenderEditor sender={editing === 'new' ? null : editing} onDone={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />;
  }
  if (!data) return <Loading error={error} />;
  const byCountry = ['CZ', 'SK', 'RO'].map((c) => ({ c, items: data.filter((s) => s.country === c) }));
  return (
    <>
      <div className="p1-mkt-settings-toolbar">
        <Button type="button" variant="secondary" size="sm" disabled title="Synchronizace z Mailkitu - připravuje se">Načíst z Mailkitu</Button>
        <Button type="button" size="sm" onClick={() => setEditing('new')}>Nový odesílatel</Button>
      </div>
      <div className="p1-mkt-sender-grid">
        {byCountry.map(({ c, items }) => (
          <ArcPanel className="p1-mkt-country-panel" key={c}>
              <div className="p1-mkt-country-head"><strong>{FLAG[c]} {c}</strong><span>{senderCountLabel(items.length)}</span></div>
              {PURPOSE_GROUPS.map((g) => {
                const arr = items.filter((s) => s.purpose === g.key);
                return (
                  <div key={g.key} className="p1-mkt-purpose-group">
                    <div className="p1-mkt-purpose-label">{g.label}</div>
                    {arr.length ? arr.map((s) => <SenderCard key={s.id} s={s} onEdit={() => setEditing(s)} />) : <div className="p1-mkt-empty-group">Žádný odesílatel</div>}
                  </div>
                );
              })}
          </ArcPanel>
        ))}
      </div>
    </>
  );
}

function MailTab() {
  const { data, error } = useList<MailTemplate>(getMailTemplates);
  if (!data) return <Loading error={error} />;
  return (
    <ArcTableFrame className="p1-mkt-table-panel" label="Mail šablony" sticky="container">
    <table className="table table-hover align-middle p1-mkt-table">
      <thead><tr className="text-muted" style={{ fontSize: '.72rem', textTransform: 'uppercase' }}><th>Kód</th><th>Jazyk</th><th>Název</th><th>Předmět</th><th>Určení</th><th>Aktivní</th><th className="text-end">Posláno</th></tr></thead>
      <tbody>
        {data.map((t) => (
          <tr key={t.id}><td><code>{t.code}</code></td><td>{t.lang}</td><td>{t.name}</td><td className="text-muted">{t.subject ?? '-'}</td>
            <td>{t.purpose === 'campaign' ? 'Kampaně' : 'Notifikační'}</td>
            <td>{t.enabled ? <span className="badge bg-success-subtle text-success">Ano</span> : <span className="badge bg-secondary-subtle text-secondary">Ne</span>}</td>
            <td className="text-end">{t.sent_total.toLocaleString('cs-CZ')}</td></tr>
        ))}
        {data.length === 0 && <tr><td colSpan={7} className="text-muted text-center py-3">Žádné mail šablony.</td></tr>}
      </tbody>
    </table>
    </ArcTableFrame>
  );
}

function ModalTab() {
  const { data, error } = useList<ModalTemplate>(() => getModalTemplates());
  if (!data) return <Loading error={error} />;
  return (
    <ArcTableFrame className="p1-mkt-table-panel" label="Modal šablony" sticky="container">
    <table className="table table-hover align-middle p1-mkt-table">
      <thead><tr className="text-muted" style={{ fontSize: '.72rem', textTransform: 'uppercase' }}><th>ID</th><th>Název (kód)</th><th>Trh</th><th>Titulek</th><th>Téma</th><th>Aktivní</th></tr></thead>
      <tbody>
        {data.map((t) => (
          <tr key={t.id}><td>{t.id}</td><td>{t.name} <span className="text-muted small">({t.code})</span></td>
            <td>{t.country ? `${FLAG[t.country] ?? ''} ${t.country}` : 'univerzální'}</td><td>{t.title ?? '-'}</td><td>{t.theme === 'dark' ? 'Tmavý' : 'Světlý'}</td>
            <td>{t.active ? <span className="badge bg-success-subtle text-success">Ano</span> : <span className="badge bg-secondary-subtle text-secondary">Ne</span>}</td></tr>
        ))}
        {data.length === 0 && <tr><td colSpan={6} className="text-muted text-center py-3">Žádné modal šablony (legacy snapshot je neobsahoval - lze přidat přes API).</td></tr>}
      </tbody>
    </table>
    </ArcTableFrame>
  );
}

function PartnersTab() {
  const { data, error } = useList<Partner>(() => getPartners());
  if (!data) return <Loading error={error} />;
  return (
    <ArcTableFrame className="p1-mkt-table-panel" label="Partneři URL" sticky="container">
    <table className="table table-hover align-middle p1-mkt-table p1-mkt-table--partners">
      <thead><tr className="text-muted" style={{ fontSize: '.72rem', textTransform: 'uppercase' }}><th>Slug</th><th>Název</th><th>Trh</th></tr></thead>
      <tbody>
        {data.map((p) => (
          <tr key={p.id}><td><code>{p.slug}</code></td><td>{p.name}</td><td>{p.country ? `${FLAG[p.country] ?? ''} ${p.country}` : '-'}</td></tr>
        ))}
        {data.length === 0 && <tr><td colSpan={3} className="text-muted text-center py-3">Žádní partneři.</td></tr>}
      </tbody>
    </table>
    </ArcTableFrame>
  );
}

// Metriky cílení - jen ty, které backend buildSegmentWhere reálně vyhodnocuje
// nad rondo_users (1:1 s segment-rules.ts). Neznámé se fail-safe ignorují.
const SEG_METRICS: { v: string; label: string }[] = [
  { v: 'days_since_active', label: 'Dní od poslední aktivity' },
  { v: 'days_since_bet', label: 'Dní od poslední sázky' },
  { v: 'account_age_days', label: 'Stáří účtu (dny)' },
  { v: 'balance', label: 'Zůstatek rondonů' },
  { v: 'cash_balance', label: 'Cash zůstatek' },
  { v: 'credits', label: 'Kredity' },
  { v: 'super_hrac_score', label: 'Super hráč skóre' },
  { v: 'tipovacka_bet_count_total', label: 'Počet sázek (tipovačka)' },
  { v: 'survey_lead_count', label: 'Počet survey leadů' },
  { v: 'subscribed', label: 'Odběr mailů (1/0)' },
];
const SEG_OPS = ['>=', '<=', '>', '<', '=='];

function SegmentsTab() {
  const [list, setList] = useState<Segment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Segment | 'new' | null>(null);

  function reload() {
    getSegments()
      .then(setList)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(() => { reload(); }, []);

  if (editing) {
    return <SegmentEditor seg={editing === 'new' ? null : editing} onDone={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />;
  }
  if (!list) return <Loading error={error} />;
  return (
    <>
      <div className="p1-mkt-settings-toolbar"><Button type="button" size="sm" onClick={() => setEditing('new')}>Nový segment</Button></div>
      <ArcTableFrame className="p1-mkt-table-panel" label="Segmenty" sticky="container">
      <table className="table table-hover align-middle p1-mkt-table">
        <thead><tr className="text-muted" style={{ fontSize: '.72rem', textTransform: 'uppercase' }}><th>Název</th><th>Trh</th><th>Priorita</th><th>Pravidla (cílení)</th><th>Aktivní</th><th></th></tr></thead>
        <tbody>
          {list.map((s) => (
            <tr key={s.id}>
              <td><span className="badge" style={{ background: s.color, color: '#fff' }}>{s.name}</span></td>
              <td>{FLAG[s.country] ?? ''} {s.country}</td><td>{s.priority}</td>
              <td className="text-muted" style={{ fontSize: '.78rem' }}>{(s.rules ?? []).map((r) => { const rr = r as { metric?: string; op?: string; value?: unknown }; return `${rr.metric ?? '?'} ${rr.op ?? ''} ${rr.value ?? ''}`; }).join(' & ') || '-'}</td>
              <td>{s.active ? <span className="badge bg-success-subtle text-success">Ano</span> : <span className="badge bg-secondary-subtle text-secondary">Ne</span>}</td>
              <td className="text-end"><Button type="button" variant="secondary" size="sm" onClick={() => setEditing(s)}>Upravit</Button></td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={6} className="text-muted text-center py-3">Žádné segmenty.</td></tr>}
        </tbody>
      </table>
      </ArcTableFrame>
    </>
  );
}

function SegmentEditor({ seg, onDone, onCancel }: { seg: Segment | null; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(seg?.name ?? '');
  const [country, setCountry] = useState(seg?.country ?? 'CZ');
  const [priority, setPriority] = useState(seg?.priority ?? 100);
  const [active, setActive] = useState(seg?.active ?? true);
  const [rules, setRules] = useState<SegmentRuleInput[]>(
    () => ((seg?.rules as { metric?: string; op?: string; value?: unknown }[]) ?? [])
      .map((r) => ({ metric: r.metric ?? 'days_since_active', op: r.op ?? '>=', value: Number(r.value) || 0 })),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<number | null>(null);

  // Živý odhad jen u existujícího segmentu (estimate je per uložené id).
  useEffect(() => {
    if (!seg) return;
    getSegmentEstimate(seg.id).then((r) => setEstimate(r.count)).catch(() => setEstimate(null));
  }, [seg]);

  const setRule = (i: number, patch: Partial<SegmentRuleInput>) => setRules(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  async function save() {
    if (!name.trim()) { setErr('Název je povinný'); return; }
    setSaving(true); setErr(null);
    try {
      const body = { name: name.trim(), country, priority: Number(priority), active, rules };
      if (seg) await updateSegment(seg.id, body); else await createSegment(body);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Uložení selhalo'); setSaving(false);
    }
  }

  return (
    <ArcPanel className="p1-mkt-editor">
      <h2>{seg ? `Upravit segment: ${seg.name}` : 'Nový segment'}</h2>
      {err && <div className="alert alert-danger py-2">{err}</div>}
      <div className="row g-2 mb-3">
        <div className="col-sm-6"><label className="form-label small text-muted mb-1">Název *</label>
          <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="col-sm-3"><label className="form-label small text-muted mb-1">Trh</label>
          <CompactSelect value={country} disabled={!!seg} ariaLabel="Trh" options={['CZ', 'SK', 'RO'].map((v) => ({ value: v, label: `${FLAG[v]} ${v}` }))} onChange={setCountry} /></div>
        <div className="col-sm-3"><label className="form-label small text-muted mb-1">Priorita</label>
          <input type="number" className="form-control form-control-sm" value={priority} onChange={(e) => setPriority(Number(e.target.value))} /></div>
      </div>

      <div className="p1-mkt-rules-head">
        <strong className="small">Pravidla cílení</strong>
        <span className="text-muted small ms-2">(VŠECHNA musí platit - AND)</span>
        <Button type="button" variant="secondary" size="sm" onClick={() => setRules([...rules, { metric: 'days_since_active', op: '>=', value: 7 }])}>Přidat pravidlo</Button>
      </div>
      {rules.length === 0 && <div className="text-muted small mb-2">Bez pravidel = celý trh {country}.</div>}
      {rules.map((r, i) => (
        <div className="p1-mkt-rule-row" key={i}>
          <CompactSelect value={r.metric} ariaLabel={`Metrika pravidla ${i + 1}`} options={SEG_METRICS.map((m) => ({ value: m.v, label: m.label }))} onChange={(v) => setRule(i, { metric: v })} />
          <CompactSelect value={r.op} ariaLabel={`Operátor pravidla ${i + 1}`} options={SEG_OPS.map((o) => ({ value: o, label: o }))} onChange={(v) => setRule(i, { op: v })} />
          <input type="number" className="form-control form-control-sm" style={{ maxWidth: 120 }} value={r.value} onChange={(e) => setRule(i, { value: Number(e.target.value) })} />
          <Button type="button" variant="danger" size="sm" aria-label={`Odebrat pravidlo ${i + 1}`} onClick={() => setRules(rules.filter((_, j) => j !== i))}>Odebrat</Button>
        </div>
      ))}

      <div className="form-check form-switch mt-2 mb-3">
        <input className="form-check-input" type="checkbox" id="seg-active" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <label className="form-check-label small" htmlFor="seg-active">Aktivní</label>
      </div>
      {seg && <div className="wz-recipients mb-3" style={{ fontSize: '.82rem', padding: '8px 12px', background: 'rgba(0,113,227,.06)', border: '1px solid rgba(0,113,227,.18)', color: '#0058b3', borderRadius: 8 }}>Aktuální odhad příjemců: <strong>{estimate == null ? '-' : `${estimate.toLocaleString('cs-CZ')} uživatelů`}</strong> <span className="text-muted">(přepočítá se po uložení nových pravidel)</span></div>}

      <div className="p1-mkt-editor-actions">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={saving}>Zrušit</Button>
        <Button type="button" size="sm" onClick={save} disabled={saving}>{saving ? 'Ukládám…' : 'Uložit'}</Button>
      </div>
    </ArcPanel>
  );
}
